// Shared column-sort state machine for the tracking tabs.
//
// Three tabs each grew their own click-to-cycle sort implementation with
// subtly different shapes: Insurance tracks a `cleared` flag so a
// default-active column can distinguish "never touched" (renders
// ascending) from "explicitly cleared" (renders none); Milestone always
// has an active column and only toggles ascending/descending, never
// clearing; Deliverable persists the sort in the URL instead of local
// React state. This module is the single source of truth for the cycling
// + aria-sort semantics, configurable enough to reproduce all three
// existing behaviours exactly (this unifies the implementation, not the
// UX — each tab keeps its current on-screen behaviour).
//
// The `persisted` option is the URL adapter: when supplied, the hook reads
// state from `persisted.value` and writes through `persisted.onChange`
// instead of owning React state, so a tab whose sort round-trips through
// `useSearchParams` can still share the cycling/aria-sort logic.

import { useCallback, useState } from "react";

export type SortDirection = "ascending" | "descending";

export interface SortState<Column extends string> {
  column: Column | null;
  direction: SortDirection | null;
  /** True once the user has explicitly cleared an active sort back to
   *  column=null/direction=null. Only meaningful when `defaultColumn` is
   *  configured: it distinguishes "cleared" (aria-sort="none" on the
   *  default column) from "never touched" (the default column still
   *  reads aria-sort="ascending" until the user interacts). Tabs with no
   *  default column can ignore this field. */
  cleared: boolean;
}

export interface UseTableSortOptions<Column extends string> {
  /** Column that renders as ascending-sorted until the user explicitly
   *  changes the sort (Insurance's "coveragePeriod" default). Omit for
   *  tabs with no default-active column. */
  defaultColumn?: Column;
  /** When false, the third click on the active column (past descending)
   *  returns straight to ascending instead of clearing to column=null —
   *  Milestone's always-has-an-active-column, 2-state toggle. Defaults to
   *  true, matching Insurance/Deliverable's 3-state cycle-with-clear. */
  allowClear?: boolean;
  /** Optional external store (typically a URL-persisted state object).
   *  When supplied the hook is fully controlled: `sortState` reads
   *  `persisted.value` and every mutation calls `persisted.onChange`
   *  instead of touching local state. */
  persisted?: {
    value: SortState<Column>;
    onChange: (next: SortState<Column>) => void;
  };
}

/** Pure transition: the next sort state after a click on `column`, given
 *  the current state and cycle configuration. Exported standalone so a
 *  tab whose header already computes the next state itself (e.g. a
 *  per-column click handler) can still share the exact same rule instead
 *  of re-deriving it. */
export function nextTableSortState<Column extends string>(
  column: Column,
  state: SortState<Column>,
  options: Pick<UseTableSortOptions<Column>, "defaultColumn" | "allowClear"> = {},
): SortState<Column> {
  const { defaultColumn, allowClear = true } = options;
  const isUntouchedDefault =
    state.column === null && state.direction === null && !state.cleared;

  // Default-column edge case: when state is the untouched default (no
  // explicit sort yet) and the user clicks the default column, the visible
  // sort is already ascending — so the click flips straight to descending
  // instead of re-applying ascending.
  if (defaultColumn !== undefined && column === defaultColumn && isUntouchedDefault) {
    return { column, direction: "descending", cleared: false };
  }

  if (state.column === column) {
    if (state.direction === "ascending") {
      return { column, direction: "descending", cleared: false };
    }
    if (allowClear) {
      return { column: null, direction: null, cleared: true };
    }
    return { column, direction: "ascending", cleared: false };
  }

  // Switching to a different sortable column always starts at ascending.
  return { column, direction: "ascending", cleared: false };
}

/** The aria-sort value a given column's header should render, given the
 *  current sort state and cycle configuration. */
export function tableSortAriaSort<Column extends string>(
  column: Column,
  state: SortState<Column>,
  options: Pick<UseTableSortOptions<Column>, "defaultColumn"> = {},
): "ascending" | "descending" | "none" {
  if (state.column === column) {
    return state.direction === "descending" ? "descending" : "ascending";
  }
  if (
    options.defaultColumn !== undefined &&
    column === options.defaultColumn &&
    state.column === null &&
    state.direction === null &&
    !state.cleared
  ) {
    return "ascending";
  }
  return "none";
}

export interface UseTableSortResult<Column extends string> {
  sortState: SortState<Column>;
  ariaSortFor: (column: Column) => "ascending" | "descending" | "none";
  /** Click-to-cycle a column through the configured transition. */
  handleSortClick: (column: Column) => void;
  /** Direct setter for callers that already have a fully-computed next
   *  state (e.g. a tab whose sortable-header component performs its own
   *  cycling and reports the result). */
  setSortState: (next: SortState<Column>) => void;
}

export function useTableSort<Column extends string>(
  initial: SortState<Column>,
  options: UseTableSortOptions<Column> = {},
): UseTableSortResult<Column> {
  const [localState, setLocalState] = useState<SortState<Column>>(initial);
  const sortState = options.persisted ? options.persisted.value : localState;

  const setSortState = useCallback(
    (next: SortState<Column>) => {
      if (options.persisted) {
        options.persisted.onChange(next);
      } else {
        setLocalState(next);
      }
    },
    [options],
  );

  const handleSortClick = useCallback(
    (column: Column) => {
      setSortState(nextTableSortState(column, sortState, options));
    },
    [sortState, options, setSortState],
  );

  return {
    sortState,
    ariaSortFor: (column) => tableSortAriaSort(column, sortState, options),
    handleSortClick,
    setSortState,
  };
}
